<template>
  <b-card>
    <!-- Info -->
    <validation-observer
      ref="form"
      v-slot="{ handleSubmit }"
    >
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('pages.email') }}
        </b-col>
        <b-col cols="9">
          <validation-provider
            #default="{ errors }"
            :name="$t('usersettings.email')"
            vid="email"
            :rules="{ required: true, email: true, min: 3, max: 100 }"
          >
            <b-form-input
              v-model="company_email"
              :state="isValidated && errors.length > 0 ? false : null"
              :readonly="readOnly || !isOwner"
              :placeholder="$t('pages.email')"
            />
            <small
              v-if="isValidated && errors.length > 0"
              class="text-danger"
            >{{ errors[0] }}</small>
            <small
              v-else-if="!readOnly && !isOwner"
              class="text-muted"
            >{{ $t('usersettings.onlyTheAccountOwnerCanChangeTheCompanyEmail') }}</small>
          </validation-provider>
        </b-col>
      </b-row>

      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('pages.company') }}
        </b-col>
        <b-col cols="9">
          <validation-provider
            #default="{ errors }"
            :name="$t('usersettings.company')"
            vid="company"
            :rules="{ required: true, min: 3, max: 100 }"
          >
            <b-form-input
              v-model="company"
              :state="isValidated && errors.length > 0 ? false : null"
              :readonly="readOnly"
              :placeholder="$t('pages.company')"
            />
            <small
              v-if="isValidated && errors.length > 0"
              class="text-danger"
            >{{ errors[0] }}</small>
          </validation-provider>
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('pages.firstName') }}
        </b-col>
        <b-col cols="9">
          <validation-provider
            #default="{ errors }"
            :name="$t('usersettings.firstName')"
            vid="first name"
            :rules="{ required: true, min: 3, max: 100 }"
          >
            <b-form-input
              v-model="first_name"
              :state="isValidated && errors.length > 0 ? false : null"
              :readonly="readOnly"
              :placeholder="$t('usersettings.firstName')"
            />
            <small
              v-if="isValidated && errors.length > 0"
              class="text-danger"
            >{{ errors[0] }}</small>
          </validation-provider>
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('pages.lastName') }}
        </b-col>
        <b-col cols="9">
          <b-form-input
            v-model="last_name"
            :readonly="readOnly"
            :placeholder="$t('usersettings.lastName')"
          />
        </b-col>
      </b-row>

      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('usersettings.addressLine1') }}
        </b-col>
        <b-col cols="9">
          <b-form-input
            v-model="address_line1"
            :readonly="readOnly"
            :placeholder="$t('usersettings.addressLine1')"
          />
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('usersettings.addressLine2') }}
        </b-col>
        <b-col cols="9">
          <b-form-input
            v-model="address_line2"
            :readonly="readOnly"
            :placeholder="$t('usersettings.addressLine2')"
          />
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('usersettings.zipCode') }}
        </b-col>
        <b-col cols="9">
          <b-form-input
            v-model="zip_code"
            :readonly="readOnly"
            :placeholder="$t('usersettings.zipCode')"
          />
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('pages.city') }}
        </b-col>
        <b-col cols="9">
          <b-form-input
            v-model="city"
            :readonly="readOnly"
            :placeholder="$t('pages.city')"
          />
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('dashboard.country') }}
        </b-col>
        <b-col cols="9">
          <b-form-select
            v-model="country"
            :options="countries"
            :disabled="readOnly"
            text-field="name"
            value-field="code"
            :placeholder="$t('dashboard.country')"
          />
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('usersettings.phoneNumber') }}
        </b-col>
        <b-col cols="9">
          <b-form-input
            v-model="phone_number"
            :readonly="readOnly"
            :placeholder="$t('usersettings.phoneNumber')"
          />
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="3">
          {{ $t('usersettings.skypeUsername') }}
        </b-col>
        <b-col cols="9">
          <b-form-input
            v-model="skype"
            :readonly="readOnly"
            :placeholder="$t('usersettings.skypeUsername')"
          />
        </b-col>
      </b-row>

      <b-row v-if="!readOnly">
        <b-col>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-2 mr-1"
            @click="isValidated = true; handleSubmit(saveGeneralInformation)"
          >
            {{ $t('apps.saveChanges') }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            type="reset"
            class="mt-2"
            @click.prevent="reset()"
          >
            {{ $t('usersettings.reset') }}
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>
  </b-card>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import { ValidationObserver, ValidationProvider } from 'vee-validate/dist/vee-validate.full'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    profile: {
      type: Object,
      // Fixed ESLint: Prop 'profile' requires default value
      default: () => ({}),
    },
    countries: {
      type: Array,
      // Fixed ESLint: Prop 'countries' requires default value
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isValidated: false,
      first_name: '',
      last_name: '',
      company_email: '',
      company: '',
      address_line1: '',
      address_line2: '',
      zip_code: '',
      city: '',
      country: '',
      phone_number: '',
      skype: '',
    }
  },
  computed: {
    isOwner() {
      return this.$store.getters['auth/isOwner']
    },
  },
  watch: {
    profile: {
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.fetchDataFromStore()
        } else {
          this.clearForm()
        }
      },
    },
  },
  methods: {
    saveGeneralInformation() {
      // Safety check for readOnly
      if (this.readOnly) return

      this.showLoading()
      this.$http
        .put('/api/profile-settings/update-general-information', {
          first_name: this.first_name,
          last_name: this.last_name,
          email: this.company_email,
          company: this.company,
          address_line1: this.address_line1,
          address_line2: this.address_line2,
          zip_code: this.zip_code,
          city: this.city,
          country: this.country,
          phone_number: this.phone_number,
          skype: this.skype,
        })
        .then(response => {
          const updatedProfile = response.data?.profile
          if (updatedProfile && updatedProfile.email !== this.$store.getters['auth/appActiveUser'].email) {
            this.$store.dispatch('auth/updateAdminEmail', updatedProfile.email)
          }

          this.$emit('refresh-profile')
          this.alertSuccess(response.data.message)
        })
        .finally(() => {
          this.hideLoading()
        })
    },
    reset() {
      this.isValidated = false
      this.fetchDataFromStore()
    },
    fetchDataFromStore() {
      this.first_name = this.profile.first_name
      this.last_name = this.profile.last_name
      this.company_email = this.profile.company_email || this.profile.email
      this.company = this.profile.company
      this.address_line1 = this.profile.address_line1
      this.address_line2 = this.profile.address_line2
      this.zip_code = this.profile.zip_code
      this.city = this.profile.city
      this.country = this.profile.country
      this.phone_number = this.profile.phone_number
      this.skype = this.profile.skype
    },
    clearForm() {
      this.first_name = ''
      this.last_name = ''
      this.company_email = ''
      this.company = ''
      this.address_line1 = ''
      this.address_line2 = ''
      this.zip_code = ''
      this.city = ''
      this.country = ''
      this.phone_number = ''
      this.skype = ''
    },

  },
}
</script>
